import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Role } from '../../types';

// 仮の監査ログデータ型定義
interface AuditLog {
  id: string;
  timestamp: string;
  userId: string;
  userEmail: string;
  userName: string;
  action: string;
  targetType: string;
  targetId: string;
  ipAddress: string;
  userAgent: string;
  details: Record<string, unknown>;
  severity: 'info' | 'warning' | 'error';
}

const AuditLogsPage: React.FC = () => {
  const { user } = useAuth();
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedAction, setSelectedAction] = useState<string>('all');

  useEffect(() => {
    // 仮の監査ログデータ（実装準備中）
    const mockAuditLogs: AuditLog[] = [
      {
        id: '1',
        timestamp: '2025-08-12T09:30:00Z',
        userId: 'user-123',
        userEmail: 'admin@example.com',
        userName: '管理者A',
        action: 'create_company_admin',
        targetType: 'user',
        targetId: 'admin-456',
        ipAddress: '192.168.1.100',
        userAgent: 'Mozilla/5.0...',
        details: {
          companyName: 'テスト陸送株式会社',
          email: 'admin@test-company.jp',
        },
        severity: 'info',
      },
      {
        id: '2',
        timestamp: '2025-08-12T08:15:00Z',
        userId: 'user-456',
        userEmail: 'super@rakuhai.jp',
        userName: 'ラクハイ管理者',
        action: 'login_attempt_failed',
        targetType: 'auth',
        targetId: 'login-789',
        ipAddress: '203.0.113.10',
        userAgent: 'Mozilla/5.0...',
        details: {
          email: 'wrong@email.com',
          reason: 'invalid_credentials',
        },
        severity: 'warning',
      },
      {
        id: '3',
        timestamp: '2025-08-12T07:45:00Z',
        userId: 'user-789',
        userEmail: 'dispatcher@transport.jp',
        userName: '配車担当B',
        action: 'delete_case',
        targetType: 'case',
        targetId: 'case-001',
        ipAddress: '192.168.1.50',
        userAgent: 'Mozilla/5.0...',
        details: {
          caseName: '東京→大阪 車両輸送',
          deleteReason: 'canceled_by_client',
        },
        severity: 'error',
      },
    ];

    setTimeout(() => {
      setAuditLogs(mockAuditLogs);
      setIsLoading(false);
    }, 1000);
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'info':
        return 'text-blue-600 bg-blue-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'info':
        return <InformationCircleIcon className='h-4 w-4' />;
      case 'warning':
        return <ExclamationTriangleIcon className='h-4 w-4' />;
      case 'error':
        return <ExclamationTriangleIcon className='h-4 w-4' />;
      default:
        return <InformationCircleIcon className='h-4 w-4' />;
    }
  };

  const getActionDisplayName = (action: string) => {
    const actionMap: Record<string, string> = {
      create_company_admin: '管理者作成',
      login_attempt_failed: 'ログイン失敗',
      delete_case: '案件削除',
      update_company: '会社情報更新',
      create_user: 'ユーザー作成',
      login_success: 'ログイン成功',
      logout: 'ログアウト',
      update_case: '案件更新',
      create_case: '案件作成',
    };
    return actionMap[action] || action;
  };

  // super_admin権限チェック
  if (!user || user.role !== Role.SUPER_ADMIN) {
    return <Navigate to='/' replace />;
  }

  const filteredLogs = auditLogs.filter(log => {
    const matchesSearch =
      searchQuery === '' ||
      log.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.userEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getActionDisplayName(log.action).toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSeverity = selectedSeverity === 'all' || log.severity === selectedSeverity;
    const matchesAction = selectedAction === 'all' || log.action === selectedAction;

    return matchesSearch && matchesSeverity && matchesAction;
  });

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      {/* ヘッダー */}
      <div className='bg-white dark:bg-gray-800 shadow'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-6'>
            <div>
              <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>監査ログ</h1>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                システムのセキュリティ監査とユーザー活動の追跡
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* 実装準備中メッセージ */}
        <div className='mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
          <div className='flex items-center'>
            <InformationCircleIcon className='h-5 w-5 text-blue-400 mr-2' />
            <p className='text-sm text-blue-800 dark:text-blue-200'>
              <strong>実装準備中:</strong>{' '}
              現在はサンプルデータを表示しています。実際の監査ログ機能はSprint 3で実装予定です。
            </p>
          </div>
        </div>

        {/* フィルター・検索セクション */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg mb-6'>
          <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white'>フィルター・検索</h3>
          </div>
          <div className='p-6'>
            <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
              {/* 検索バー */}
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' />
                </div>
                <input
                  type='text'
                  placeholder='ユーザー名、メール、アクションで検索...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className='block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm'
                />
              </div>

              {/* 重要度フィルター */}
              <div>
                <select
                  value={selectedSeverity}
                  onChange={e => setSelectedSeverity(e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm'
                >
                  <option value='all'>全ての重要度</option>
                  <option value='info'>情報</option>
                  <option value='warning'>警告</option>
                  <option value='error'>エラー</option>
                </select>
              </div>

              {/* アクションフィルター */}
              <div>
                <select
                  value={selectedAction}
                  onChange={e => setSelectedAction(e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm'
                >
                  <option value='all'>全てのアクション</option>
                  <option value='create_company_admin'>管理者作成</option>
                  <option value='login_attempt_failed'>ログイン失敗</option>
                  <option value='delete_case'>案件削除</option>
                  <option value='login_success'>ログイン成功</option>
                  <option value='logout'>ログアウト</option>
                </select>
              </div>

              {/* ログエクスポート（未実装） */}
              <div>
                <button
                  disabled
                  className='w-full inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <DocumentTextIcon className='h-4 w-4 mr-2' />
                  CSVエクスポート（準備中）
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 監査ログテーブル */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden'>
          <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
              監査ログ一覧 ({filteredLogs.length}件)
            </h3>
          </div>
          <div className='overflow-x-auto'>
            {filteredLogs.length > 0 ? (
              <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                <thead className='bg-gray-50 dark:bg-gray-700'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      <div className='flex items-center'>
                        <CalendarIcon className='h-4 w-4 mr-1' />
                        日時
                      </div>
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      <div className='flex items-center'>
                        <UserIcon className='h-4 w-4 mr-1' />
                        ユーザー
                      </div>
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      アクション
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      重要度
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      詳細
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      IPアドレス
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                  {filteredLogs.map(log => (
                    <tr key={log.id} className='hover:bg-gray-50 dark:hover:bg-gray-700'>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                        {new Date(log.timestamp).toLocaleString('ja-JP')}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm font-medium text-gray-900 dark:text-white'>
                          {log.userName}
                        </div>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>
                          {log.userEmail}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                        {getActionDisplayName(log.action)}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(log.severity)}`}
                        >
                          {getSeverityIcon(log.severity)}
                          <span className='ml-1'>
                            {log.severity === 'info' && '情報'}
                            {log.severity === 'warning' && '警告'}
                            {log.severity === 'error' && 'エラー'}
                          </span>
                        </span>
                      </td>
                      <td className='px-6 py-4 text-sm text-gray-900 dark:text-white'>
                        <button
                          className='inline-flex items-center text-primary-600 hover:text-primary-500'
                          onClick={() => {
                            // 詳細表示モーダル（未実装）
                            alert(
                              '詳細表示機能は実装準備中です\n\n' +
                                JSON.stringify(log.details, null, 2)
                            );
                          }}
                        >
                          <EyeIcon className='h-4 w-4 mr-1' />
                          詳細表示
                        </button>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                        {log.ipAddress}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className='text-center py-12'>
                <DocumentTextIcon className='mx-auto h-12 w-12 text-gray-400' />
                <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-white'>
                  監査ログが見つかりません
                </h3>
                <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                  検索条件を変更してください
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogsPage;
